import Head from "next/head";
import { useTranslation } from "next-i18next";
import { ViewModel } from "../../lib/viewModel/createViewModel";
import { InvestorEventPageStory } from "../../lib/storyblok/types/stories/InvestorEventPageStory";
import { StoryblokStoryComponent } from "../../lib/storyblok/types/StoryblokComponent";
import Agenda from "../../ui/Agenda";
import Details from "../../ui/Details";
import WithSidebar from "../../ui/PageTemplates/WithSidebar";
import Share from "../../ui/Share";
import Tags from "../../ui/Tags";
import { DynamicBlocks } from "../DynamicBlock";
import FeaturedHero from "../../ui/FeaturedHero";
import Button from "../../ui/Button";
import { useEffect, useState } from "react";
import { useLinks } from "../../lib/storyblok/useLinks";
import Tag from "../Tag";
import ShareItems from "../../ui/Share/components/ShareItems";
import { useViewmodel } from "../../lib/storyblok/ViewmodelContext";
import Image from "next/image";
import WeChat from "../../public/images/wechat.jpg";
import Body from "../../ui/PageTemplates/WithSidebar/components/Body";
import { MediaQueryHider } from "../../ui/MediaQueryHider/MediaQueryHider";
import { FormatDateRange } from "../../lib/utils/FormatDateRange";
import EditorialWithoutSpacing from "../Nestable/EditorialWithoutSpacing";
import { storyblokEditable } from "@storyblok/react";
import { deduceDomain } from "../../lib/utils/deduceDomain";
import Alert from "../../ui/Common/Alert";
import { isValidImageData, imageSizes } from "../../lib/utils/imageUtils";
import StoryblokImage from "../StoryblokImage";
import { useSeoImage } from "../../hooks/useSeoImage";

type VM = ViewModel<InvestorEventPageStory>;

const { Sidebar } = WithSidebar;

const InvestorEventPage: StoryblokStoryComponent<VM> = ({ story }) => {
  const {
    heroImage,
    heroTitle,
    heroPreamble,
    heroButtons,
    callToActionText,
    noHeroOverlay,
    eventDate,
    startDate,
    eventLocation,
    eventAvailability,
    eventLanguages,
    details,
    relatedDocuments,
    contacts,
    editorial,
    agendaTitle,
    agenda,
    resourcesFromEvent,
    speakers,
    callToActionSection,
    contentGrid,
    eventType,
    taggedProducts,
    taggedSolutions,
  } = story.content;

  const resolveLink = useLinks();

  const { t } = useTranslation(["eventpage", "tags", "common"]);

  const { globalLinks, locale, site } = useViewmodel();

  const [eventHasExpired, setEventHasExpired] = useState(false);

  const hasEventExpired = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today > new Date(date);
  };

  useEffect(() => {
    if (eventDate) {
      setEventHasExpired(hasEventExpired(eventDate));
    }
  }, [eventDate]);

  const { seoContentImage, seoFallbackImage } = useSeoImage({
    seoImage: story.content.seoImage,
    locale,
    site,
  });

  // Event schema
  // Source: https://developers.google.com/search/docs/appearance/structured-data/event
  const eventInvestorSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: heroTitle || "",
    startDate: startDate || "",
    endDate: eventDate || "",
    eventAttendanceMode: "https://schema.org/MixedEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    location: {
      "@type": "Place",
      name: eventLocation || "",
    },
    image: seoContentImage || seoFallbackImage,
    description: heroPreamble || story.content.seoDescription,
    organizer: {
      "@type": "Organization",
      name: "Tobii",
      url: deduceDomain(locale, site),
    },
  };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(eventInvestorSchema),
          }}
        />
      </Head>
      <FeaturedHero
        backgroundImage={
          heroImage &&
          isValidImageData(heroImage) && (
            <StoryblokImage
              priority
              image={heroImage}
              layout="fill"
              alt={heroTitle}
              sizes={imageSizes("100vw", {
                desktop: "1200px",
                xl: "1600px",
              })}
            />
          )
        }
        label={
          eventType?.length > 0 ? t(eventType[0], { ns: "tags" }) : undefined
        }
        title={heroTitle}
        preamble={heroPreamble}
        date={eventDate}
        startDate={startDate}
        location={eventLocation}
        buttons={
          <>
            {callToActionText && (
              <Button
                variant="inverted"
                disabled={eventHasExpired}
                href="#cta-section"
              >
                {callToActionText}
              </Button>
            )}
            {heroButtons?.length > 0 && <DynamicBlocks blocks={heroButtons} />}
          </>
        }
        noOverlay={noHeroOverlay}
      />
      <WithSidebar>
        <Body>
          <MediaQueryHider hideAboveDesktop>
            {eventHasExpired && (
              <Alert
                variant="info"
                title={t("the-event-has-concluded", { ns: "eventpage" })}
              />
            )}
            <Details>
              {eventDate && (
                <Details.Detail
                  icon="symbolicon-calendar"
                  title={
                    startDate
                      ? FormatDateRange(startDate, eventDate)
                      : FormatDateRange(eventDate)
                  }
                />
              )}

              {eventLocation && (
                <Details.Detail
                  icon="location"
                  title={eventLocation}
                  subtitle={t(eventAvailability, { ns: "tags" })}
                />
              )}
              {eventLanguages && eventLanguages.length > 0 && (
                <Details.Detail
                  icon="world"
                  title={eventLanguages
                    .map((tag) => t(tag, { ns: "common" }))
                    .join(", ")}
                />
              )}
              {details &&
                details.length > 0 &&
                details.map((d) => (
                  <div key={d._uid} {...storyblokEditable(d)}>
                    <Details.Detail
                      icon={d.icon}
                      title={d.title}
                      subtitle={d.subtitle}
                    />
                  </div>
                ))}
            </Details>
          </MediaQueryHider>

          {editorial && (
            <DynamicBlocks
              blocks={editorial}
              overrides={{ editorial: EditorialWithoutSpacing }}
            />
          )}
          {agenda?.length >= 1 && (
            <Agenda title={agendaTitle}>
              {agenda.map((i) => (
                <div key={i._uid} {...storyblokEditable(i)}>
                  <Agenda.Item time={i.time} description={i.description} />
                </div>
              ))}
            </Agenda>
          )}
        </Body>
        <Sidebar>
          <MediaQueryHider hideBelowDesktop>
            {eventHasExpired && (
              <Alert
                variant="info"
                title={t("the-event-has-concluded", { ns: "eventpage" })}
              />
            )}
            <Details>
              {eventDate && (
                <Details.Detail
                  icon="symbolicon-calendar-date"
                  title={
                    startDate
                      ? FormatDateRange(startDate, eventDate)
                      : FormatDateRange(eventDate)
                  }
                />
              )}
              {eventLocation && (
                <Details.Detail
                  icon="location"
                  title={eventLocation}
                  subtitle={t(eventAvailability, { ns: "tags" })}
                />
              )}
              {eventLanguages && eventLanguages.length > 0 && (
                <Details.Detail
                  icon="world"
                  title={eventLanguages
                    .map((tag) => t(tag, { ns: "common" }))
                    .join(", ")}
                />
              )}

              {details &&
                details.length > 0 &&
                details.map((d) => (
                  <div key={d._uid} {...storyblokEditable(d)}>
                    <Details.Detail
                      key={d._uid}
                      icon={d.icon}
                      title={d.title}
                      subtitle={d.subtitle}
                    />
                  </div>
                ))}
            </Details>
          </MediaQueryHider>

          {relatedDocuments && relatedDocuments.length > 0 && (
            <Details isRelatedDocuments>
              {relatedDocuments.map((d) => (
                <div key={d._uid} {...storyblokEditable(d)}>
                  <Details.Document
                    icon={d.icon}
                    title={d.title}
                    subtitle={d.subtitle}
                    href={resolveLink(d.link)}
                  />
                </div>
              ))}
            </Details>
          )}
          {(eventType || taggedProducts || taggedSolutions) && (
            <Tags>
              {eventType && eventType.length > 0 && (
                <Tags.TagList title={t("event-type")}>
                  {eventType?.map((tag) => {
                    const baseUrl = globalLinks.investorEventFilterPage
                      ? resolveLink(globalLinks.investorEventFilterPage)
                      : "";
                    const url = `${baseUrl}?types=${tag}#filter`;
                    return (
                      <Tag key={tag} tag={t(tag, { ns: "tags" })} url={url} />
                    );
                  })}
                </Tags.TagList>
              )}
            </Tags>
          )}
          <DynamicBlocks blocks={contacts} />
          <Share
            weChat={
              locale === "zh" && (
                <Image src={WeChat} quality={100} sizes="100vw" alt="WeChat" />
              )
            }
          >
            <ShareItems />
          </Share>
        </Sidebar>
      </WithSidebar>
      {resourcesFromEvent && <DynamicBlocks blocks={resourcesFromEvent} />}
      {speakers && <DynamicBlocks blocks={speakers} />}
      {callToActionSection && (
        <div id="cta-section">
          <DynamicBlocks blocks={callToActionSection} />
        </div>
      )}
      {contentGrid && <DynamicBlocks blocks={contentGrid} />}
    </>
  );
};

export default InvestorEventPage;
